/**
 * EDITING STYLES BANK — Registry de estilos visuais para vídeos Remotion
 *
 * Cada estilo define a "cara" completa do vídeo:
 * palette, spring configs, transitions, timing, layout, elements, fonts.
 *
 * Uso: selecionar estilo no início da produção, aplicar em todas as cenas.
 * Baseado em: remotion-dev/trailer, av/remotion-bits, claude-code-video-toolkit
 */

// ─── SPRING PROFILES (do Remotion Trailer — validados) ────────────────
export const SPRINGS = {
  /** Snappy — reveals rápidos, sem bounce. damping:200 */
  smooth: {damping: 200},
  /** UI elements — minimal bounce. damping:20, stiffness:200 */
  snappy: {damping: 20, stiffness: 200},
  /** Playful — bounce visível. damping:8 */
  bouncy: {damping: 8},
  /** Slow & heavy — logo, títulos hero. damping:15, stiffness:80, mass:2 */
  heavy: {damping: 15, stiffness: 80, mass: 2},
  /** Code/technical — precise, no overshoot. stiffness:200, damping:100, mass:0.5 */
  precise: {damping: 100, stiffness: 200, mass: 0.5, overshootClamping: true},
} as const;

export type SpringProfile = keyof typeof SPRINGS;

// ─── FONT SCALE (mínimos enforçáveis — canvas 1080x1920) ─────────────
export const FONT_SCALE = {
  /** Hero number / impact visual */
  hero: {min: 96, max: 200, weight: 900},
  /** Heading principal da cena */
  h1: {min: 64, max: 96, weight: 800},
  /** Subheading / statement */
  h2: {min: 48, max: 64, weight: 700},
  /** Body text / parágrafos */
  body: {min: 48, max: 56, weight: 500},
  /** Labels / badges / category */
  label: {min: 28, max: 36, weight: 600},
  /** Caption / legenda (NUNCA menor que 32) */
  caption: {min: 32, max: 40, weight: 500},
  /** CTA button text */
  cta: {min: 36, max: 48, weight: 700},
} as const;

// ─── SAFE ZONES (px do canvas 1080x1920) ──────────────────────────────
export const SAFE_ZONES = {
  /** Universal — funciona em todas as plataformas */
  universal: {top: 180, bottom: 480, left: 60, right: 60},
  /** TikTok (atualizado jan/2026) */
  tiktok: {top: 160, bottom: 480, left: 48, right: 48},
  /** Instagram Reels */
  instagram: {top: 200, bottom: 400, left: 48, right: 48},
  /** YouTube Shorts */
  youtube: {top: 140, bottom: 360, left: 48, right: 48},
} as const;

// ─── TIMING RULES ─────────────────────────────────────────────────────
export const TIMING = {
  /** Transição entre cenas (frames) */
  sceneTransition: {min: 10, standard: 20, dramatic: 45},
  /** Stagger entre elementos (frames) */
  stagger: {tight: 2, standard: 4, wide: 8},
  /** Hold de texto na tela (frames) — tempo de leitura */
  textHold: {
    short: 45,    // 1.5s — frase curta (< 5 palavras)
    standard: 75, // 2.5s — frase normal (5-12 palavras)
    long: 120,    // 4s — frase longa ou dado complexo
  },
  /** Hook máximo (frames antes de perder audiência) */
  hookMax: 90, // 3s @ 30fps
  /** Micro-pausa entre cenas (frames de "respiração") */
  breathe: 6,
} as const;

// ─── ASSET SOURCES (de onde buscar mídia) ─────────────────────────────
export const ASSET_SOURCES = {
  /** B-roll vídeo — Pexels API (MP4, 720p-4K, sem atribuição) */
  brollVideo: "pexels",
  /** Fotos HQ — Unsplash (fotos), Pexels (fotos + vídeo) */
  photos: ["unsplash", "pexels"],
  /** Imagens geradas — NanoBanana (Gemini Pro) */
  generated: "nanobanana",
  /** Vídeo gerado — LTX-2 (AI text-to-video) */
  generatedVideo: "ltx2",
  /** Música — ACE-Step 1.5 ou ElevenLabs */
  music: ["acestep", "elevenlabs"],
  /** TTS — ElevenLabs (voz Guilherme: 60FHDyXNkQf05pwCUyfS) */
  tts: "elevenlabs",
} as const;

// ─── EDITING STYLES ───────────────────────────────────────────────────

export interface EditingStyle {
  id: string;
  name: string;
  description: string;
  /** Quando usar este estilo */
  useWhen: string;
  /** Palette de cores */
  palette: {
    bg: string;
    bgAlt: string;
    text: string;
    textMuted: string;
    accent: string;
    accentAlt: string;
  };
  /** Visual density — quanto da tela é preenchido */
  density: "minimal" | "balanced" | "dense";
  /** Proporção de tipos de cena */
  sceneTypes: {
    /** Texto puro (lettering) */
    textOnly: number;
    /** Texto + elemento visual */
    textPlusElement: number;
    /** Visual dominante (B-roll, imagem, 3D) */
    visualDominant: number;
  };
  /** Springs dominantes */
  springs: {
    entrance: SpringProfile;
    exit: SpringProfile;
    emphasis: SpringProfile;
  };
  /** Transições preferidas */
  transitions: string[];
  /** Elementos visuais permitidos */
  elements: string[];
  /** FPS */
  fps: 30 | 60;
  /** Ratio B-roll vs motion graphics */
  brollRatio: number; // 0 = pure motion, 1 = pure B-roll
}

export const EDITING_STYLES: Record<string, EditingStyle> = {
  // ─── 1. CINEMATIC ────────────────────────────────────────────
  cinematic: {
    id: "cinematic",
    name: "Cinematic",
    description:
      "Ritmo lento, visual imersivo, texto grande centralizado. Pausas dramáticas. B-roll dominante com overlay de texto. Inspirado em documentários e trailers de cinema.",
    useWhen:
      "Verdades Difíceis, Aprendi Errando, branding/posicionamento, conteúdo emocional",
    palette: {
      bg: "#000000",
      bgAlt: "#0a0a0a",
      text: "#ffffff",
      textMuted: "#8a8a8a",
      accent: "#f5ad43",
      accentAlt: "#fd764a",
    },
    density: "minimal",
    sceneTypes: {textOnly: 20, textPlusElement: 30, visualDominant: 50},
    springs: {entrance: "heavy", exit: "smooth", emphasis: "bouncy"},
    transitions: ["crossfade", "lightLeak", "zoomBlur"],
    elements: [
      "gradient-text",
      "vignette",
      "film-grain",
      "b-roll-overlay",
      "ken-burns",
      "parallax",
      "counter-hero",
    ],
    fps: 30,
    brollRatio: 0.5,
  },

  // ─── 2. DYNAMIC ──────────────────────────────────────────────
  dynamic: {
    id: "dynamic",
    name: "Dynamic",
    description:
      "Ritmo rápido, cortes secos, stagger agressivo. Texto entra com velocidade. Ideal para conteúdo que precisa prender atenção. Backgrounds variam por cena.",
    useWhen:
      "Café com teu ROI, Perguntas, conteúdo de engajamento, reels curtos (15-30s)",
    palette: {
      bg: "#0a0a0a",
      bgAlt: "#1a0505",
      text: "#f5f5f7",
      textMuted: "#a0a0a8",
      accent: "#00DF82",
      accentAlt: "#FECB18",
    },
    density: "balanced",
    sceneTypes: {textOnly: 40, textPlusElement: 45, visualDominant: 15},
    springs: {entrance: "snappy", exit: "smooth", emphasis: "bouncy"},
    transitions: ["slide", "glitch", "rgbSplit", "cut"],
    elements: [
      "animated-text",
      "pills",
      "number-badge",
      "progress-bar",
      "accent-line",
      "scan-line",
      "particles",
    ],
    fps: 30,
    brollRatio: 0.1,
  },

  // ─── 3. MOTION GRAPHICS ─────────────────────────────────────
  motionGraphics: {
    id: "motionGraphics",
    name: "Motion Graphics",
    description:
      "Animação complexa, elementos gráficos abstratos, transições elaboradas. O visual É o conteúdo — não depende de texto. Inspirado em MoGraph de estúdio.",
    useWhen:
      "Anúncios, product demos, showcases, conteúdo de branding premium",
    palette: {
      bg: "#0f0f23",
      bgAlt: "#1a1a3e",
      text: "#ffffff",
      textMuted: "#c4b5fd",
      accent: "#8b5cf6",
      accentAlt: "#06b6d4",
    },
    density: "dense",
    sceneTypes: {textOnly: 10, textPlusElement: 30, visualDominant: 60},
    springs: {entrance: "snappy", exit: "snappy", emphasis: "precise"},
    transitions: ["clockWipe", "pixelate", "checkerboard", "slide"],
    elements: [
      "3d-elements",
      "particle-system",
      "gradient-mesh",
      "svg-morph",
      "lottie",
      "charts",
      "device-mockup",
    ],
    fps: 60,
    brollRatio: 0,
  },

  // ─── 4. EDITORIAL ────────────────────────────────────────────
  editorial: {
    id: "editorial",
    name: "Editorial",
    description:
      "Tipografia forte como protagonista. Inspirado em revistas, NYT, Apple Keynotes. Contraste de peso tipográfico (thin 200 vs black 900). Fundos limpos.",
    useWhen:
      "Verdades Difíceis com dados, listas, frameworks. Conteúdo intelectual que precisa ser lido.",
    palette: {
      bg: "#ffffff",
      bgAlt: "#f5f5f5",
      text: "#0a0a0a",
      textMuted: "#6b7280",
      accent: "#3b82f6",
      accentAlt: "#ef4444",
    },
    density: "minimal",
    sceneTypes: {textOnly: 60, textPlusElement: 35, visualDominant: 5},
    springs: {entrance: "smooth", exit: "smooth", emphasis: "heavy"},
    transitions: ["crossfade", "wipe", "slide"],
    elements: [
      "gradient-text",
      "split-screen",
      "counter",
      "typewriter",
      "pull-quote",
      "data-card",
    ],
    fps: 30,
    brollRatio: 0,
  },

  // ─── 5. PRODUCT DEMO ────────────────────────────────────────
  productDemo: {
    id: "productDemo",
    name: "Product Demo",
    description:
      "Screen recording + overlays explicativos. PiP do narrador. Zoom em features. Música de fundo sutil. Inspirado em videos de lançamento SaaS.",
    useWhen:
      "Demos de produto, tutoriais, walkthroughs, onboarding videos",
    palette: {
      bg: "#0f172a",
      bgAlt: "#1e293b",
      text: "#f8fafc",
      textMuted: "#94a3b8",
      accent: "#3b82f6",
      accentAlt: "#10b981",
    },
    density: "balanced",
    sceneTypes: {textOnly: 10, textPlusElement: 20, visualDominant: 70},
    springs: {entrance: "smooth", exit: "smooth", emphasis: "snappy"},
    transitions: ["crossfade", "slide", "zoomBlur"],
    elements: [
      "device-mockup",
      "pip-narrator",
      "zoom-highlight",
      "cursor-animation",
      "label-badge",
      "lower-third",
      "code-block",
    ],
    fps: 30,
    brollRatio: 0,
  },

  // ─── 6. B-ROLL HEAVY ────────────────────────────────────────
  brollHeavy: {
    id: "brollHeavy",
    name: "B-Roll Heavy",
    description:
      "Vídeo stock como protagonista. Texto aparece como overlay com fundo semitransparente. Ken Burns em fotos. Narração conduz, visual ilustra. Inspirado em documentários curtos.",
    useWhen:
      "Storytelling visual, Aprendi Errando com cenas reais, conteúdo emocional forte",
    palette: {
      bg: "#000000",
      bgAlt: "rgba(0,0,0,0.7)",
      text: "#ffffff",
      textMuted: "#d1d5db",
      accent: "#fbbf24",
      accentAlt: "#f97316",
    },
    density: "minimal",
    sceneTypes: {textOnly: 5, textPlusElement: 25, visualDominant: 70},
    springs: {entrance: "heavy", exit: "smooth", emphasis: "smooth"},
    transitions: ["crossfade", "lightLeak", "wipe"],
    elements: [
      "b-roll-fullscreen",
      "text-overlay-box",
      "ken-burns",
      "film-grain",
      "vignette",
      "lower-third",
      "captions",
    ],
    fps: 30,
    brollRatio: 0.7,
  },

  // ─── 7. QUIZ / GAMIFIED ─────────────────────────────────────
  quizGamified: {
    id: "quizGamified",
    name: "Quiz / Gamified",
    description:
      "Interativo, glassmorphism, timer, confetti. Alto ritmo, cascading entries, respostas com reveal dramático. 60fps para suavidade.",
    useWhen: "Quiz, perguntas, polls, gamificação, conteúdo de engajamento",
    palette: {
      bg: "#000000",
      bgAlt: "#1e1b4b",
      text: "#f5f5f7",
      textMuted: "#86868b",
      accent: "#00DF82",
      accentAlt: "#FECB18",
    },
    density: "balanced",
    sceneTypes: {textOnly: 20, textPlusElement: 70, visualDominant: 10},
    springs: {entrance: "snappy", exit: "precise", emphasis: "bouncy"},
    transitions: ["slide", "glitch", "pixelate"],
    elements: [
      "glassmorphism-card",
      "option-pill",
      "timer-bar",
      "confetti",
      "progress-ring",
      "counter-hero",
      "tv-static",
    ],
    fps: 60,
    brollRatio: 0,
  },

  // ─── 8. TALKING HEAD ────────────────────────────────────────
  talkingHead: {
    id: "talkingHead",
    name: "Talking Head Edit",
    description:
      "Vídeo gravado do apresentador como base. Jump cuts automáticos. Captions TikTok-style. Lower third. Zoom dinâmico em frases-chave. Música sutil.",
    useWhen:
      "Café com teu ROI (gravado no carro), depoimentos, entrevistas, podcast clips",
    palette: {
      bg: "#000000",
      bgAlt: "#111111",
      text: "#ffffff",
      textMuted: "#a0a0a0",
      accent: "#39E508",
      accentAlt: "#ffffff",
    },
    density: "minimal",
    sceneTypes: {textOnly: 0, textPlusElement: 15, visualDominant: 85},
    springs: {entrance: "smooth", exit: "smooth", emphasis: "snappy"},
    transitions: ["cut", "crossfade"],
    elements: [
      "tiktok-captions",
      "lower-third",
      "jump-cut",
      "zoom-punch",
      "emoji-pop",
      "progress-bar",
    ],
    fps: 30,
    brollRatio: 0,
  },
} as const;

export type EditingStyleId = keyof typeof EDITING_STYLES;

// ─── STYLE SELECTOR (recomenda estilo por linha editorial) ────────────
export const STYLE_BY_EDITORIAL: Record<string, EditingStyleId[]> = {
  "verdades-dificeis": ["cinematic", "editorial", "dynamic"],
  "cafe-com-teu-roi": ["dynamic", "talkingHead", "editorial"],
  "aprendi-errando": ["cinematic", "brollHeavy", "editorial"],
  "perguntas": ["dynamic", "editorial", "quizGamified"],
  "quiz": ["quizGamified"],
  "anuncio": ["motionGraphics", "productDemo", "dynamic"],
  "demo": ["productDemo"],
  "branding": ["cinematic", "motionGraphics"],
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
